import { useState } from 'react';
import { getPlayerAvatar } from '../../utils/player';
import GameDetailsModal from './GameDetailsModal';
import mvpShimmer from '../../assets/mvp_shimmer.webp';

// Bundled + preloaded at startup so the animated badge never pops in (the old Firebase GIF was
// 1.27MB and served with max-age=0, so it re-downloaded every time).
if (typeof window !== 'undefined') {
  const preload = new Image();
  preload.src = mvpShimmer;
}

const SessionHistoryView = ({
  session,
  allPlayers,
  onDeleteSession,
  isArchived,
  canEdit,
}) => {
  const [showGameDetails, setShowGameDetails] = useState(false);

  return (
    <div className="p-0 rounded-xl flex flex-col justify-between min-h-[400px]">
      <div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Player</th>
                <th>W</th>
                <th>L</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const statsMap = session.sessionStats || {};
                const totalWinsMap = {};
                let maxWins = 0;

                Object.entries(statsMap).forEach(([player, data]) => {
                  const wins = data.wins || 0;
                  totalWinsMap[player] = wins;
                  if (wins > maxWins) {
                    maxWins = wins;
                  }
                });

                // Calculate win percentage for tie-breaking
                const playerPercentages = {};
                Object.entries(statsMap).forEach(([player, data]) => {
                  const wins = data.wins || 0;
                  const losses = data.losses || 0;
                  const totalGames = wins + losses;
                  playerPercentages[player] = totalGames > 0 ? (wins / totalGames) * 100 : 0;
                });

                // Find highest win percentage among top winners
                const topWinnersPercentages = Object.entries(statsMap)
                  .filter(([_, data]) => (data.wins || 0) === maxWins)
                  .map(([player, _]) => playerPercentages[player]);
                const highestWinPercentage = Math.max(...topWinnersPercentages, 0);

                return Object.entries(statsMap)
                  .map(([player, data]) => ({
                    player,
                    data,
                    winPercentage: playerPercentages[player],
                  }))
                  .sort((a, b) => {
                    // If MVP is set and not "Tie", MVP player always goes to top
                    if (session.mvp && session.mvp !== "Tie") {
                      if (a.player === session.mvp) return -1;
                      if (b.player === session.mvp) return 1;
                    }
                    
                    // If MVP is "Tie", all top players (with highest win %) go to top
                    if (session.mvp === "Tie") {
                      const aIsTop = a.winPercentage === highestWinPercentage;
                      const bIsTop = b.winPercentage === highestWinPercentage;
                      if (aIsTop && !bIsTop) return -1;
                      if (!aIsTop && bIsTop) return 1;
                    }
                    
                    // Sort by win percentage (descending)
                    if (a.winPercentage !== b.winPercentage) {
                      return b.winPercentage - a.winPercentage;
                    }
                    
                    // Tie-breaker: alphabetically
                    return a.player.localeCompare(b.player);
                  })
                  .map(({ player, data, winPercentage }) => {
                    const winPercentageDisplay =
                      data.wins + data.losses > 0 ? `${winPercentage.toFixed(1)}%` : '0%';
                    return (
                      <tr key={player}>
                        <td>
                          <div className="flex items-center gap-sm">
                            <img
                              src={getPlayerAvatar(allPlayers, player)}
                              alt={`${player}'s avatar`}
                              className="avatar avatar-sm"
                            />
                            <span>{player}</span>
                          </div>
                        </td>
                        <td>{data.wins}</td>
                        <td>{data.losses}</td>
                        <td>{winPercentageDisplay}</td>
                      </tr>
                    );
                  });
              })()}
            </tbody>
          </table>
        </div>

        {isArchived && (session.ranked === false) && (
          <p className="caption text-center mt-md" style={{ color: 'var(--on-surface-variant)' }}>
            Unranked Session
          </p>
        )}

        {isArchived && session.mvp && session.ranked !== false && (
          <div className="mt-md flex flex-col items-center justify-center">
            <div className="mvp-badge-container">
              <img
                src={mvpShimmer}
                alt="MVP badge"
                width="72"
                height="30"
                decoding="sync"
                className="mvp-badge-image"
              />
            </div>
            <span className="headline-medium">{session.mvp === "Tie" ? "Tie" : session.mvp}</span>
          </div>
        )}
      </div>

      {isArchived && (
        <div className="flex flex-col gap-md mt-md">
          <button
            onClick={() => setShowGameDetails(true)}
            className="btn btn-secondary btn-full"
          >
            <span className="btn-text">GAME DETAILS</span>
          </button>

          {canEdit && (
            <button
              onClick={onDeleteSession}
              className="btn btn-danger btn-full"
            >
              <span className="btn-text">DELETE SESSION</span>
            </button>
          )}
        </div>
      )}

      {showGameDetails && (
        <GameDetailsModal
          session={session}
          allPlayers={allPlayers}
          onClose={() => setShowGameDetails(false)}
        />
      )}
    </div>
  );
};

export default SessionHistoryView;

